import { StandardEvents } from '@shopify/events';
import './eroe-components.js';
import './eroe-privacy.js';
import { cartIsBusy, cartNeedsReview, reserveCartWork, subscribeCart, waitForCart } from './eroe-cart.js';

const config = JSON.parse(document.getElementById('eroe-config')?.textContent || '{}');
export const eroeText = (key) => config.messages?.[key] || key;

// Reserve on the first input event, before native debounced handlers schedule their writes.
const reservations = new Map();
document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof Element) || !input.matches('cart-note textarea, cart-items-component input[name="updates[]"]')) return;
  const previous = reservations.get(input);
  const release = previous?.release || reserveCartWork();
  clearTimeout(previous?.timer);
  reservations.set(input, { release, timer: setTimeout(() => {
    reservations.delete(input);
    release();
  }, 650) });
}, true);

subscribeCart(({ pending, blocked }) => {
  document.documentElement.toggleAttribute('data-eroe-cart-busy', pending > 0);
  document.documentElement.toggleAttribute('data-eroe-cart-review', blocked);
  for (const notice of document.querySelectorAll('[data-eroe-cart-status]')) {
    notice.hidden = !pending && !blocked;
    const text = notice.querySelector('[data-eroe-status-text]');
    if (text) text.textContent = eroeText(blocked ? 'cart_review' : 'cart_pending');
  }
});

// Native form submit is preserved. A checkout click waits for both debounces and network writes.
let waitingCheckout = false;
document.addEventListener('submit', async (event) => {
  const button = event.submitter;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !(button instanceof HTMLButtonElement) || button.name !== 'checkout') return;
  if (!cartIsBusy() && !cartNeedsReview()) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (waitingCheckout) return;
  waitingCheckout = true;
  await waitForCart();
  waitingCheckout = false;
  if (cartNeedsReview()) {
    window.location.assign(Theme.routes.cart_url);
  } else {
    // Section rendering can replace the form and its external submitter while waiting.
    const current = document.querySelector('button[name="checkout"]');
    if (current instanceof HTMLButtonElement && current.form) current.form.requestSubmit(current);
    else window.location.assign(Theme.routes.cart_url);
  }
}, true);

// Do not trap keyboard users in a non-modal desktop drawer. Native modal dialogs retain their trap.
const synchronizeOverlays = () => {
  const open = !!document.querySelector('dialog[open], theme-drawer[open], header-menu details[open], [data-eroe-overlay="open"]');
  const focusedInput = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
  document.documentElement.toggleAttribute('data-eroe-hide-sticky', open || !!focusedInput);
};
document.addEventListener('toggle', synchronizeOverlays, true);
document.addEventListener('focusin', synchronizeOverlays);
document.addEventListener('focusout', () => queueMicrotask(synchronizeOverlays));
new MutationObserver(synchronizeOverlays).observe(document.body, {
  attributes: true, attributeFilter: ['open', 'data-eroe-overlay'], subtree: true,
});
synchronizeOverlays();

// Checkout also waits for integrations which publish Shopify's standard cart promises.
for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartNoteUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(name, (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    const release = reserveCartWork();
    event.promise.then(release, release);
  });
}
